import { InvoiceStatusEnum, InvoiceSummaryResp } from '@monetize/types/app';
import {
  AppColumnDef,
  createColumnHelper,
} from '../../components/Monetize/DataTableNew/appTable';
import { actionCatalogColumn } from '../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  currencyBodyTemplate,
  dateBodyTemplate,
  dateTimeBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  nameWithIdBodyTemplate,
} from '../../components/Monetize/DataTableNew/tableUtilsNew';
import { InvoiceStatusEnumDisplay } from '../../constants/invoice';
import {
  getAccountDetailRoute,
  getBillGroupPageRoute,
  getInvoiceDetailRoute,
} from '../../constants/routes';
import { getTaxCalculationError } from '../../utils/invoices';
import { statusWithErrorTooltipTemplate } from '../../utils/tableUtils';
import { InvoiceDueDateCell } from './InvoiceDueDateCell';

const columnHelper = createColumnHelper<InvoiceSummaryResp>();

export interface InvoiceListColumnContext extends ColumnContextBase<InvoiceSummaryResp> {
  /** Account id used to scope the Bill Group record-search filter. */
  accountId: string;
  /** Whether to show the Account Name column (account-scoped vs. global). */
  showAccount: boolean;
  /** Whether to omit the Bill Group column. */
  omitBillGroup: boolean;
}

/** tableId: FilterStateKeys.INVOICE_LIST */
export const INVOICE_LIST_CATALOG: ColumnCatalog<
  InvoiceSummaryResp,
  InvoiceListColumnContext
> = [
  {
    id: 'invoiceNumber',
    label: 'Invoice Number',
    customizable: false,
    build: () =>
      columnHelper.accessor('invoiceNumber', {
        ...getColHeaderWithSize('Invoice Number', 220),
        ...getColHeaderFilters('TEXT', {
          cellStyles: {},
        }),
        cell: nameWithIdBodyTemplate<InvoiceSummaryResp, unknown>({
          idProp: 'id',
          nameProp: 'invoiceNumber',
          idLinkFn: (id) => getInvoiceDetailRoute(id),
        }),
        enableSorting: true,
      }),
  },
  {
    id: 'accountName',
    label: 'Account Name',
    isAvailable: ({ showAccount }) => showAccount,
    build: () => {
      // Field is account.accountName, but filter requires accountName
      const accountCol: AppColumnDef<InvoiceSummaryResp, any> = {
        accessorKey: 'accountName',
        ...getColHeaderWithSize('Account Name', 200),
        ...getColHeaderFilters('TEXT'),
        cell: nameWithIdBodyTemplate<any, any>({
          idProp: 'account.id' as any,
          nameProp: 'account.accountName' as any,
          idLinkFn: (id) => getAccountDetailRoute(id),
        }),
        enableSorting: true,
      };
      return accountCol;
    },
  },
  {
    id: 'billGroupId',
    label: 'Bill Group',
    isAvailable: ({ omitBillGroup }) => !omitBillGroup,
    build: ({ accountId }) =>
      columnHelper.accessor('billGroupId', {
        ...getColHeaderWithSize('Bill Group', 180),
        ...getColHeaderFilters('RECORD_SEARCH', {
          filterKey: 'billGroupId',
          recordSearch: {
            endpoint: '/api/billGroups/summary',
            itemTitle: 'name',
            itemValue: 'id',
            label: 'Filter by Bill Group',
            sort: 'name',
            additionalSearchParams: {
              accountId,
            },
          },
        }),
        cell: nameWithIdBodyTemplate<InvoiceSummaryResp, any>({
          idProp: 'billGroup.id',
          nameProp: 'billGroup.name',
          idLinkFn: (id) => getBillGroupPageRoute(id),
        }),
        enableSorting: false,
      }),
  },
  {
    id: 'invoicingDate',
    label: 'Invoicing Date',
    build: () =>
      columnHelper.accessor('invoicingDate', {
        ...getColHeaderWithSize('Invoicing Date', 180),
        ...getColHeaderFilters('DATE'),
        cell: dateBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'dueDate',
    label: 'Due Date',
    build: () =>
      columnHelper.accessor('dueDate', {
        ...getColHeaderWithSize('Due Date', 140),
        ...getColHeaderFilters('DATE'),
        cell: (info) => (
          <InvoiceDueDateCell
            dueDate={info.row.original.dueDate}
            status={info.row.original.status}
          />
        ),
        enableSorting: true,
      }),
  },
  {
    id: 'lastSent',
    label: 'Sent Date',
    build: () =>
      columnHelper.accessor('lastSent', {
        ...getColHeaderWithSize('Sent Date', 140),
        ...getColHeaderFilters('DATE'),
        cell: dateBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            InvoiceStatusEnum,
            InvoiceStatusEnumDisplay,
          ),
        }),
        cell: (info) =>
          statusWithErrorTooltipTemplate<InvoiceSummaryResp, InvoiceStatusEnum>(
            'status',
            'taxError',
            InvoiceStatusEnumDisplay,
            getTaxCalculationError,
          )(info.row.original),
        enableSorting: true,
      }),
  },
  {
    id: 'amount',
    label: 'Amount',
    build: () =>
      columnHelper.accessor('amount', {
        ...getColHeaderWithSize('Amount', 150),
        ...getColHeaderFilters('NUMBER'),
        cell: currencyBodyTemplate({ currencyProperty: 'currency' }),
        enableSorting: true,
      }),
  },
  {
    id: 'amountDue',
    label: 'Amount Due',
    build: () =>
      columnHelper.accessor('amountDue', {
        ...getColHeaderWithSize('Amount Due', 150),
        ...getColHeaderFilters('NUMBER'),
        cell: currencyBodyTemplate({ currencyProperty: 'currency' }),
        enableSorting: true,
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        ...getColHeaderWithSize('Updated', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        ...getColHeaderWithSize('Created', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  actionCatalogColumn<InvoiceSummaryResp>(),
];
